// Payment Success screen with EMAIL and CHECKBOXES to send receipt and newsletter.
// After Continuar or sending receipt: show confirmation only — no redirect to admin/login.

import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Mail, Star, CheckCircle } from 'lucide-react';
import PrimaryLogo from '../../../assets/fonts/Primary logo.png';
import { confirmPayment } from '../../../services/firebase/functions';
import { useRestaurant } from '../../../providers/RestaurantContext';

const PaymentSuccessOptionsScreen: React.FC = () => {
  const location = useLocation();
  const { transactionId, restaurantId, amount, mesaId, restaurantName, serverName, staffId } = location.state || {};
  const { restaurant } = useRestaurant();
  const resolvedRestaurantId = restaurantId || restaurant?.id;
  
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [email, setEmail] = useState('');
  const [wantsNews, setWantsNews] = useState(false);
  const [wantsFiscal, setWantsFiscal] = useState(false);
  const [rnc, setRnc] = useState('');
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);

  const handleSendReceipt = async () => {
    if (!resolvedRestaurantId) {
      alert('Error: No se encontró información del restaurante');
      return;
    }

    setSendingReceipt(true);

    try {
      await confirmPayment({
        restaurantId: resolvedRestaurantId,
        mesaId: String(mesaId),
        selectedItemIds: [],
        subtotal: Number(amount || 0),
        email: email
      });
      
      alert('¡Recibo enviado exitosamente!');
    } catch (error: any) {
      console.error('Error sending receipt:', error);
      alert('Error al enviar el recibo. Por favor intenta de nuevo.');
    } finally {
      setSendingReceipt(false);
      setShowReceiptModal(false);
      setShowCompletionMessage(true);
    }
  };

  const handleContinue = () => {
    setShowCompletionMessage(true);
  };

  // Final confirmation: no redirects, customer can close the page
  const finishFlow = () => setShowCompletionMessage(true);

  if (showCompletionMessage) {
    return (
      <div className="min-h-screen bg-[#F2F2F7] flex flex-col">
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-gray-50/80 border-b border-gray-200/50">
          <div className="max-w-lg mx-auto px-4 py-4 min-h-[3.5rem]" aria-hidden />
        </header>
        <div className="flex-1 flex flex-col items-center justify-center px-4 pb-12">
          <div className="max-w-lg w-full text-center">
            <div className="mb-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-green-600" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mb-2">
                Pago completado correctamente.
              </h2>
              <p className="text-[13px] text-gray-600 leading-relaxed">
                Puedes cerrar esta página o volver al restaurante.
              </p>
              <p className="text-[13px] text-gray-600 mt-2">Gracias por usar YAP.</p>
            </div>
            <p className="text-[11px] text-[#8E8E93]">
              Próximamente podrás descargar la app YAP para pagar aún más rápido.
            </p>
          </div>
        </div>
        <footer className="pt-2 pb-8 flex items-center justify-center gap-2 bg-[#F2F2F7]">
          <span className="text-[11px] text-[#8E8E93]">powered by</span>
          <img src={PrimaryLogo} alt="YAP" className="h-10 w-auto object-contain" />
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2F7] flex flex-col w-full overflow-x-hidden animate-in fade-in slide-in-from-bottom-4 duration-300">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-gray-50/80 border-b border-gray-200/50">
        <div className="max-w-lg mx-auto flex items-center px-4 py-4 min-h-[3.5rem]">
          <div className="w-10 h-10 flex-shrink-0" />
          <h1 className="flex-1 text-center text-lg font-semibold text-gray-900 tracking-tight">
            ¡Gracias!
          </h1>
          <div className="w-10 h-10 flex-shrink-0" />
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center max-w-lg mx-auto w-full px-4 py-8">
        {/* Success — single line, same card language as breakdown */}
        <div className="w-full bg-white rounded-xl shadow-sm border border-gray-100/50 p-5 mb-6">
          <div className="flex items-center justify-center gap-3">
            <div className="w-9 h-9 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-[15px] font-semibold text-gray-900 tracking-tight">Pago fue exitoso</p>
          </div>
        </div>

        {/* Action buttons — match PaymentBreakdown primary (Pagar) + secondary outline */}
        <div className="w-full space-y-3 pt-1">
          <button
            type="button"
            onClick={() => setShowReceiptModal(true)}
            className="w-full h-14 rounded-xl border border-gray-200 bg-white text-gray-900 text-[13px] font-semibold tracking-wide shadow-sm hover:bg-gray-50 hover:border-gray-300 transition-all flex items-center justify-center gap-2.5 active:scale-[0.99]"
          >
            <Mail className="w-5 h-5 text-gray-700" strokeWidth={2} />
            Recibir recibo por correo electrónico
          </button>

          <button
            type="button"
            onClick={handleContinue}
            className="w-full relative h-14 rounded-xl font-semibold shadow-xl shadow-gray-900/15 flex items-center justify-center gap-2.5 px-4 bg-[#1C1C1E] text-white text-[13px] font-medium tracking-wide hover:scale-[1.01] active:scale-[0.98] transition-all overflow-hidden group"
          >
            <div className="bg-white/10 p-1 rounded-lg">
              <Star className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            Continuar
          </button>
        </div>
      </main>

      <footer className="mt-auto pt-4 pb-8 flex items-center justify-center gap-2 bg-[#F2F2F7]">
        <span className="text-[11px] text-[#8E8E93]">powered by</span>
        <img src={PrimaryLogo} alt="YAP" className="h-10 w-auto object-contain" />
      </footer>

      {/* Receipt Modal */}
      {showReceiptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center">
          <div className="bg-white w-full h-[85vh] rounded-t-3xl overflow-y-auto animate-slide-up">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center">
              <button onClick={() => setShowReceiptModal(false)} className="mr-4">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h2 className="text-lg font-semibold flex-1">Recibir recibo</h2>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-5">
              <p className="text-sm text-gray-600">
                Llena los campos de abajo para recibir tu recibo
              </p>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tucorreo@ejemplo.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-base min-h-[44px]"
                />
              </div>

              {/* Newsletter Checkbox */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={wantsNews}
                  onChange={(e) => setWantsNews(e.target.checked)}
                  className="mt-1 w-5 h-5 text-blue-600 rounded"
                />
                <span className="text-sm text-gray-700">
                  Deseo recibir las últimas noticias sobre <span className="font-semibold">{restaurantName || 'este restaurante'}</span>
                </span>
              </label>

              {/* Fiscal Receipt Checkbox */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={wantsFiscal}
                  onChange={(e) => setWantsFiscal(e.target.checked)}
                  className="mt-1 w-5 h-5 text-blue-600 rounded"
                />
                <span className="text-sm text-gray-700 font-medium">
                  Comprobante fiscal
                </span>
              </label>

              {/* RNC Input (conditional) */}
              {wantsFiscal && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">RNC</label>
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={rnc}
                    onChange={(e) => setRnc(e.target.value)}
                    placeholder="000-00000-0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-base min-h-[44px]"
                  />
                </div>
              )}

              {/* Send Button */}
              <button
                onClick={handleSendReceipt}
                disabled={!email || sendingReceipt}
                className="w-full bg-black text-white py-4 rounded-xl font-semibold hover:bg-gray-800 disabled:bg-gray-400 transition-colors"
              >
                {sendingReceipt ? 'Enviando...' : 'Enviar recibo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentSuccessOptionsScreen;
